/**
 * ChecklistItemRow
 *
 * A single task row with checkbox, editable text input, and delete button.
 * Memoized to prevent unnecessary re-renders in the task list.
 */

import { useState, useRef, useCallback, memo } from 'react';
import { Alert, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { ChecklistItem } from '@/lib/utils/EventDescriptionParser';

export type ChecklistItemRowProps = {
  item: ChecklistItem;
  onToggle: (itemId: string) => void;
  onUpdateText: (itemId: string, text: string) => void;
  onRemove: (itemId: string) => void;
  onInsertAfter: (itemId: string, trailingText: string) => void;
  palette: typeof Colors.light;
  paletteTextMuted: string;
  checkboxColor: string;
  textColor?: string;
  itemRef: (id: string, ref: TextInput | null) => void;
};

export const ChecklistItemRow = memo(function ChecklistItemRow({
  item,
  onToggle,
  onUpdateText,
  onRemove,
  onInsertAfter,
  palette,
  paletteTextMuted,
  checkboxColor,
  textColor,
  itemRef,
}: ChecklistItemRowProps) {
  // Determine checkmark color: if textColor is light, use dark checkmark
  const checkmarkColor = textColor === '#FFFFFF' ? '#1A1726' : '#FFFFFF';
  const [isFocused, setIsFocused] = useState(false);
  const selectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  const handleSelectionChange = useCallback(
    (event: { nativeEvent: { selection: { start: number; end: number } } }) => {
      selectionRef.current = event.nativeEvent.selection;
    },
    []
  );

  const handleTextChange = useCallback(
    (text: string) => {
      if (text.includes('\n')) {
        const cursorPos = selectionRef.current.start;
        const originalText = item.text;
        const textBefore = originalText.slice(0, cursorPos);
        const textAfter = originalText.slice(cursorPos);
        onUpdateText(item.id, textBefore);
        onInsertAfter(item.id, textAfter);
      } else {
        onUpdateText(item.id, text);
      }
    },
    [item.id, item.text, onUpdateText, onInsertAfter]
  );

  const handleToggle = useCallback(() => {
    onToggle(item.id);
  }, [item.id, onToggle]);

  const handleRemove = useCallback(() => {
    Alert.alert(
      'Delete Task',
      'Are you sure you want to delete this task?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => onRemove(item.id)
        },
      ]
    );
  }, [item.id, onRemove]);

  const handleRef = useCallback(
    (ref: TextInput | null) => {
      itemRef(item.id, ref);
    },
    [item.id, itemRef]
  );

  return (
    <View style={styles.checklistRow}>
      <TouchableOpacity
        onPress={handleToggle}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        style={[
          styles.checkbox,
          { borderColor: checkboxColor },
          item.completed && [styles.checkboxCompleted, { backgroundColor: checkboxColor }],
        ]}
      >
        {item.completed && <Ionicons name="checkmark" size={16} color={checkmarkColor} />}
      </TouchableOpacity>

      <TextInput
        ref={handleRef}
        value={item.text}
        onChangeText={handleTextChange}
        onSelectionChange={handleSelectionChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder="Task description"
        placeholderTextColor={paletteTextMuted}
        multiline={true}
        scrollEnabled={false}
        textAlignVertical="top"
        style={[
          styles.checklistInput,
          { color: textColor ?? palette.text, fontFamily: getBodyFont() },
          item.completed && styles.completedText,
        ]}
      />

      {isFocused && (
        <TouchableOpacity onPress={handleRemove} style={styles.deleteButton}>
          <Ionicons name="close" size={18} color={paletteTextMuted} />
        </TouchableOpacity>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  checklistRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md + 2,
    paddingVertical: Spacing.sm,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: BorderRadius.xs,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  checkboxCompleted: {
    borderWidth: 0,
  },
  checklistInput: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    paddingVertical: Spacing.none,
  },
  deleteButton: {
    padding: Spacing.xs,
  },
  completedText: {
    textDecorationLine: 'line-through',
    opacity: 0.5,
  },
});
